import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "microeconomics-formulas-theorems-cheat-sheet",
  category: "subject-explainers",
  authorSlug: "uday-kumar",
  title: "Microeconomics Formula Sheet & Key Theorems: The Complete DU Reference Guide",
  description: "The ultimate cheat sheet for Intermediate Microeconomics I & II. Master Consumer Theory, Producer Theory, Market Structures, Game Theory, and Welfare Economics.",
  pubDate: "2026-08-20",
  tags: ["microeconomics", "formulas", "game-theory", "welfare"],
  readMinutes: 13,
  heroEyebrow: "Subject Explainer · Micro Formulas",
};

export function Body() {
  return (
    <article className="max-w-4xl mx-auto space-y-8">
      <header className="mb-10">
        <h1 className="text-4xl font-bold mb-4 text-primary">Microeconomics Formula Sheet &amp; Key Theorems</h1>
        <p className="text-xl text-muted-foreground">The Complete DU Reference Guide for BA (Hons) Economics Students</p>
      </header>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">1. Consumer Theory</h2>
        <p className="mb-4 text-base">Consumer theory forms the bedrock of microeconomics. Understanding how individuals maximize utility subject to budget constraints is essential.</p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-card text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-4 py-2 border border-rule">Concept</th>
                <th className="px-4 py-2 border border-rule">Formula / Condition</th>
                <th className="px-4 py-2 border border-rule">Definition &amp; When to Use</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Marginal Rate of Substitution (MRS)</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">MRS_xy = MU_x / MU_y</td>
                <td className="px-4 py-2 border border-rule">The rate at which a consumer can give up some amount of one good in exchange for another good while maintaining the same level of utility.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Utility Maximization</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">MRS_xy = P_x / P_y</td>
                <td className="px-4 py-2 border border-rule">Equating the slope of the indifference curve to the slope of the budget line. Used to find Marshallian demand functions.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Slutsky Equation</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">dx_i/dp_j = (dx_i/dp_j)_comp - x_j * (dx_i/dM)</td>
                <td className="px-4 py-2 border border-rule">Decomposes the effect of a price change into substitution and income effects.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Roy's Identity</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">x_i(p, M) = - (dV/dp_i) / (dV/dM)</td>
                <td className="px-4 py-2 border border-rule">Relates Marshallian demand to the indirect utility function V(p, M).</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Shephard's Lemma</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">h_i(p, u) = dE(p, u)/dp_i</td>
                <td className="px-4 py-2 border border-rule">Relates Hicksian (compensated) demand to the expenditure function E(p, u).</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">2. Producer Theory</h2>
        <p className="mb-4 text-base">Firms aim to maximize profits or minimize costs. The core concepts parallel consumer theory but apply to inputs and outputs.</p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-card text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-4 py-2 border border-rule">Concept</th>
                <th className="px-4 py-2 border border-rule">Formula / Condition</th>
                <th className="px-4 py-2 border border-rule">Definition &amp; When to Use</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Marginal Rate of Technical Substitution (MRTS)</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">MRTS_LK = MP_L / MP_K</td>
                <td className="px-4 py-2 border border-rule">The rate at which labor can be substituted for capital while holding output constant.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Cost Minimization</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">MRTS_LK = w / r</td>
                <td className="px-4 py-2 border border-rule">Equating the slope of the isoquant to the slope of the isocost line. Used to find conditional factor demands.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Returns to Scale</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">f(tL, tK) = t^n f(L, K)</td>
                <td className="px-4 py-2 border border-rule">If n &gt; 1: Increasing RTS. If n = 1: Constant RTS. If n &lt; 1: Decreasing RTS.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">3. Market Structures</h2>
        <p className="mb-4 text-base">Analyzing how firms behave under different competitive environments.</p>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-card text-left text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="px-4 py-2 border border-rule">Market Type</th>
                <th className="px-4 py-2 border border-rule">Key Equilibrium Condition</th>
                <th className="px-4 py-2 border border-rule">Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Perfect Competition</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">P = MC</td>
                <td className="px-4 py-2 border border-rule">Firms are price takers. Zero economic profit in the long run (P = minimum AC).</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Monopoly</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">MR = MC</td>
                <td className="px-4 py-2 border border-rule">Single seller. Price is higher and quantity is lower than under perfect competition. Mark-up depends on price elasticity of demand.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Cournot Duopoly</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">Max Pi_i given q_j</td>
                <td className="px-4 py-2 border border-rule">Firms compete on quantity simultaneously. Find the intersection of Best Response (BR) functions.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Bertrand Duopoly</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">P = MC (with identical costs)</td>
                <td className="px-4 py-2 border border-rule">Firms compete on price simultaneously. The outcome perfectly mirrors perfect competition if products are homogeneous.</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border border-rule font-medium">Stackelberg Duopoly</td>
                <td className="px-4 py-2 border border-rule font-mono text-grade">Leader max Pi given Follower's BR</td>
                <td className="px-4 py-2 border border-rule">Sequential quantity competition. Leader has a first-mover advantage.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">4. Game Theory</h2>
        <p className="mb-4 text-base">Strategic interactions between rational decision-makers.</p>
        
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Nash Equilibrium (Pure Strategy):</strong> A strategy profile where no player has an incentive to unilaterally deviate. Condition: Each player's strategy is a best response to the other players' strategies.</li>
          <li><strong>Mixed Strategy Nash Equilibrium:</strong> Players randomize their strategies. Condition: A player is indifferent between the pure strategies they are randomizing over, because they yield the same expected payoff.</li>
          <li><strong>Subgame Perfect Nash Equilibrium (SPNE):</strong> Relevant for sequential games. Found using backward induction. A strategy profile that constitutes a Nash Equilibrium in every subgame.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-4 border-b border-rule pb-2">5. Welfare Economics</h2>
        <p className="mb-4 text-base">Evaluating the economic well-being of society.</p>
        
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Pareto Efficiency:</strong> An allocation where it is impossible to make one person better off without making someone else worse off.
            <ul className="list-circle pl-6 mt-1">
              <li>Exchange Efficiency: MRS_A = MRS_B</li>
              <li>Production Efficiency: MRTS_X = MRTS_Y</li>
              <li>Product Mix Efficiency: MRT_XY = MRS_XY</li>
            </ul>
          </li>
          <li><strong>First Fundamental Theorem of Welfare Economics:</strong> Any Walrasian (competitive) equilibrium is Pareto efficient, under certain assumptions (e.g., no externalities, perfect information).</li>
          <li><strong>Second Fundamental Theorem of Welfare Economics:</strong> Any Pareto efficient allocation can be achieved as a competitive equilibrium through a lump-sum redistribution of initial endowments.</li>
          <li><strong>Social Welfare Functions:</strong> Aggregating individual utilities (U_i).
            <ul className="list-circle pl-6 mt-1">
              <li>Utilitarian: W = Sum(U_i)</li>
              <li>Rawlsian: W = Min(U_1, U_2, ..., U_n)</li>
            </ul>
          </li>
        </ul>
      </section>
      
      <p className="text-sm text-muted-foreground mt-8 border-t border-rule pt-4">Bookmark this page for quick reference before your exams. These formulas are the foundation of microeconomic analysis at DU.</p>
    </article>
  );
}


